import React, { useEffect, useState, Suspense, lazy } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
const InterviewsBanner = lazy(() => import('./components/InterviewsBanner').then(m => ({
  default: m.InterviewsBanner
})));
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0">
      <div className="flex items-center gap-2" data-id="element-1">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    language,
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const [videoOpen, setVideoOpen] = useState(false);
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  // Auto-open video popup when main page loads.
  // Wait until the browser is idle so we don't compete with initial
  // hydration of below-the-fold lazy chunks. Falls back to a longer
  // setTimeout where requestIdleCallback isn't available.
  useEffect(() => {
    if (showLanding) return;
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const open = () => setVideoOpen(true);
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: {
        timeout: number;
      }) => number;
      cancelIdleCallback?: (h: number) => void;
    }).requestIdleCallback;
    if (typeof ric === 'function') {
      idleHandle = ric(open, {
        timeout: 3500
      });
    } else {
      timeoutHandle = setTimeout(open, 2500);
    }
    return () => {
      const cic = (window as unknown as {
        cancelIdleCallback?: (h: number) => void;
      }).cancelIdleCallback;
      if (idleHandle !== undefined && typeof cic === 'function') cic(idleHandle);
      if (timeoutHandle) clearTimeout(timeoutHandle);
    };
  }, [showLanding]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6">
      <Navbar data-id="element-7" />
      <main data-id="element-8">
        <Hero data-id="element-9" />
        <ConsularBanner data-id="element-10" />
        <Mission data-id="element-11" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-13" />} data-id="element-12">
          <OurPeopleStories data-id="element-14" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-16" />} data-id="element-15">
          <MakeDifferenceCTA data-id="element-17" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-18">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-19" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-21" />} data-id="element-20">
          <NewsBlog data-id="element-22" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-24" />} data-id="element-23">
          <InterviewsBanner data-id="element-25" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-27" />} data-id="element-26">
          <MediaScroller data-id="element-28" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-30" />} data-id="element-29">
          <CommunityStrength data-id="element-31" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-33" />} data-id="element-32">
          <MissionPanels data-id="element-34" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-36" />} data-id="element-35">
          <Leadership data-id="element-37" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-39" />} data-id="element-38">
          <AdvisoryCouncil data-id="element-40" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-42" />} data-id="element-41">
          <MakeDifferenceCTA data-id="element-43" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-45" />} data-id="element-44">
          <BusinessInvestments data-id="element-46" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-48" />} data-id="element-47">
          <ConsularAffairs data-id="element-49" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-51" />} data-id="element-50">
          <SocialMediaVoices data-id="element-52" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-54" />} data-id="element-53">
          <NonprofitDirectory data-id="element-55" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-57" />} data-id="element-56">
          <HondurasQuiz data-id="element-58" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-60" />} data-id="element-59">
          <ExploreHonduras data-id="element-61" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-63" />} data-id="element-62">
          <FAQ data-id="element-64" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-65">
        <Footer data-id="element-66" />
      </Suspense>
      <Suspense fallback={null} data-id="element-67">
        <ChatbaseWidget data-id="element-68" />
      </Suspense>

      {/* Auto-popup Video Modal */}
      <AnimatePresence data-id="element-69">
        {videoOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setVideoOpen(false)} data-id="element-70">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-full max-w-md flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-71">
              <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-haa-navy" data-id="element-72">
                <button onClick={() => setVideoOpen(false)} className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors" aria-label="Close" data-id="element-73">
                  <XIcon className="w-5 h-5" data-id="element-74" />
                </button>
                <a href="https://www.instagram.com/p/DYzfE-sj4gP/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==" target="_blank" rel="noopener noreferrer" className="block group" aria-label="Open Carrera 5K event details on Instagram" data-id="element-75">
                  <img src="https://cdn.magicpatterns.com/uploads/k46cefCkcq4S17t1BUkKQ7/1000_Schools_HAA_Fundraiser.jpg" alt="Carrera 5K — Domingo 7 de Junio, 2026 · Daingerfield Island, Alexandria, VA — en beneficio de 1000 Schools, patrocinado por la Honduran-American Association" className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]" data-id="element-76" />
                </a>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap" data-id="element-77">
                <a href="https://runsignup.com/Race/VA/Alexandria/OneThousandSchools5K?fbclid=PAdGRleASDKItleHRuA2FlbQIxMQBzcnRjBmFwcF9pZA8xMjQwMjQ1NzQyODc0MTQAAaeHTyBXlXoCwjC_N7A9tOe_hOg8S3AcXl62m-qtP2O75hyqIHKuzDQ6-WVb6w_aem_0S1UsTcJ4GhlT0AZZ8aYvg" target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-nunito font-bold text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2" data-id="element-78">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-79">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" data-id="element-80" />
                  </svg>
                  Sign Up for the Race
                </a>
                <a href="https://www.instagram.com/p/DYzfE-sj4gP/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==" target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-haa-blue hover:bg-haa-navy text-white font-nunito font-bold text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2" data-id="element-81">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-82">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.336 3.608 1.311.975.975 1.249 2.242 1.311 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.336 2.633-1.311 3.608-.975.975-2.242 1.249-3.608 1.311-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.336-3.608-1.311-.975-.975-1.249-2.242-1.311-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.336-2.633 1.311-3.608.975-.975 2.242-1.249 3.608-1.311 1.266-.058 1.646-.07 4.85-.07zm0 2.063c-3.155 0-3.512.012-4.752.069-1.073.049-1.658.228-2.046.379-.514.2-.881.439-1.267.825-.386.386-.625.753-.825 1.267-.151.388-.33.973-.379 2.046-.057 1.24-.069 1.597-.069 4.752s.012 3.512.069 4.752c.049 1.073.228 1.658.379 2.046.2.514.439.881.825 1.267.386.386.753.625 1.267.825.388.151.973.33 2.046.379 1.24.057 1.597.069 4.752.069s3.512-.012 4.752-.069c1.073-.049 1.658-.228 2.046-.379.514-.2.881-.439 1.267-.825.386-.386.625-.753.825-1.267.151-.388.33-.973.379-2.046.057-1.24.069-1.597.069-4.752s-.012-3.512-.069-4.752c-.049-1.073-.228-1.658-.379-2.046-.2-.514-.439-.881-.825-1.267-.386-.386-.753-.625-1.267-.825-.388-.151-.973-.33-2.046-.379-1.24-.057-1.597-.069-4.752-.069zm0 3.516a4.258 4.258 0 110 8.516 4.258 4.258 0 010-8.516zm0 7.024a2.766 2.766 0 100-5.532 2.766 2.766 0 000 5.532zm5.418-7.193a.995.995 0 11-1.99 0 .995.995 0 011.99 0z" data-id="element-83" />
                  </svg>
                  More Info
                </a>
                <button onClick={() => setVideoOpen(false)} className="px-8 py-3 bg-white/90 hover:bg-white text-haa-navy font-nunito font-bold text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2" data-id="element-84">
                  <XIcon className="w-4 h-4" data-id="element-85" />
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [showLanding, setShowLanding] = useState(!isSectionAnchor(initialHash));
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-87" />} data-id="element-86">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-88" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-89">
        <Suspense fallback={<SectionLoader data-id="element-91" />} data-id="element-90">
          <ConsularPage data-id="element-92" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-93">
        <Suspense fallback={<SectionLoader data-id="element-95" />} data-id="element-94">
          <BusinessMapPage data-id="element-96" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-97">
        <Suspense fallback={<SectionLoader data-id="element-99" />} data-id="element-98">
          <MembersDirectoryPage data-id="element-100" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-101">
        <Suspense fallback={<SectionLoader data-id="element-103" />} data-id="element-102">
          <MemberProfilePage slug={memberSlug} data-id="element-104" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-105">
        <Suspense fallback={<SectionLoader data-id="element-107" />} data-id="element-106">
          <NewsStoryPage postId={newsPostId} data-id="element-108" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-109" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-110">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-111" />
      </LanguageProvider>
    </>;
}